import axios from 'axios';
import router from '@/router/index.ts';
import type { AxiosRequestConfig } from 'axios';

import type { AccountType } from '@/types/Account.ts';
import type { SensorDistanceSettingType } from '@/types/Sensor';
import type { BookingRequest } from '@/types/Booking';

const API_URL = import.meta.env.VITE_API_URL;

// Abstract the Axios Headers object for simplicity.
const getHeaders = (accessToken?: string, hasBlob: boolean = false) => {
  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  };

  if (hasBlob) {
    config.responseType = 'blob';
  }

  if (config.headers && accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
};

// Config overrides for processing PDFs
const getPDFConfig = (accessToken: string) => {
  // Replace default headers because we want to transform the blob.
  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    responseType: 'blob',
    transformResponse: [
      function (data) {
        const blob = new window.Blob([data], { type: 'application/pdf' });
        return window.URL.createObjectURL(blob);
      },
    ],
  };
  return config;
};

// Getting a 401 from the API means our token is invalid, best just logout
let handle_401_triggered = false;
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Don't overwrite normal password / account validation login rules.
    if (!router.currentRoute.value.path.includes('/login')) {
      if (error.response && error.response.status === 401 && !handle_401_triggered) {
        handle_401_triggered = true;
        router.push({ name: 'logout' }).catch(() => {});
      } else {
        handle_401_triggered = false;
      }
    }
    return Promise.reject(error);
  },
);

/************************************************
 *
 *
 * ACCOUNTS RELATED
 *
 *
 **********************************************/

// Basic Accounts Query. Used on Manage Accounts page.
const getAccounts = async (accessToken: string, queryParams: object) => {
  return await axios
    .post(`${API_URL}/accounts`, queryParams, getHeaders(accessToken))
    .then((resp) => {
      return resp.data.accounts;
    });
};

// Retrieves a list of accounts to be used in Dropdowns.
// (eg: Add User Modal, Filter Users by Accounts on Manage Users page)
const getAccountsList = async (accessToken: string) => {
  type Record = {
    id: number;
    display_name: string;
    sso_enabled: boolean;
  };

  let records: Record[] = [];
  let currentPage = 1;
  let totalPages = 1;

  const queryParams = {
    results_per_page: 100,
    sortBy: 'name',
    page: 1,
  };

  do {
    await axios.post(`${API_URL}/accounts`, queryParams, getHeaders(accessToken)).then((resp) => {
      const formattedData = resp.data.accounts.data.map((acct: AccountType) => {
        return {
          id: acct.id,
          display_name: acct.display_name,
          sso_enabled: acct.sso_enabled,
        };
      });

      currentPage = resp.data.accounts.current_page;
      totalPages = resp.data.accounts.last_page;
      records = records.concat(formattedData);
      queryParams.page = queryParams.page + 1;
    });
  } while (currentPage < totalPages);

  return records;
};

const getAccountNew = async (accessToken: string) => {
  return await axios.get(`${API_URL}/accounts/create`, getHeaders(accessToken)).then((resp) => {
    return resp;
  });
};

const getAccountById = async (accessToken: string, accountId: number) => {
  return await axios
    .get(`${API_URL}/accounts/${accountId}/edit`, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const getAccountSensors = async (accessToken: string, accountId: number) => {
  return await axios
    .get(`${API_URL}/sensors/account/${accountId}`, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const createAccount = async (accessToken: string, payload: object) => {
  return await axios
    .post(`${API_URL}/accounts/store`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

// Make changes to account by ID, used for quick archive/unarchive.
const updateAccountById = async (accessToken: string, accountId: number, payload: object) => {
  return await axios
    .post(`${API_URL}/accounts/${accountId}/update`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const deleteAccountById = async (accessToken: string, accountId: number) => {
  return await axios
    .delete(`${API_URL}/accounts/${accountId}/delete`, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

// Make updates to the account profile.

const getAccountProfileById = async (accessToken: string, accountId: number) => {
  return await axios
    .get(`${API_URL}/accounts/${accountId}/profile`, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const getVatValidation = async (accessToken: string, vatId: string) => {
  return await axios
    .get(`${API_URL}/accounts/vat-check/${vatId}`, getHeaders(accessToken))
    .then((resp) => {
      return resp.data;
    });
};

const updateAccountProfileById = async (
  accessToken: string,
  accountId: number,
  payload: object,
) => {
  return await axios
    .patch(`${API_URL}/accounts/${accountId}/profile`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

// Client Details for Finance

const getClientDetailsList = async (accessToken: string) => {
  return await axios.get(`${API_URL}/accounts/lists`, getHeaders(accessToken)).then((resp) => {
    return resp;
  });
};
/************************************************
 *
 *
 * API KEYS RELATED
 *
 *
 **********************************************/

// Generate API Keys

const requestAPIToken = async (accessToken: string, payload: object) => {
  return await axios
    .post(`${API_URL}/users/token`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const getAPITokens = async (accessToken: string, payload: object) => {
  const data = {
    ...getHeaders(accessToken),
    params: payload,
  };
  return await axios.get(`${API_URL}/users/tokens`, data).then((resp) => {
    return resp.data;
  });
};

const deleteKeyById = async (accessToken: string, tokenId: string) => {
  return await axios
    .delete(`${API_URL}/users/tokens/${tokenId}`, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const getAPIUsage = async (accessToken: string) => {
  return await axios.get(`${API_URL}/users/tokens/usage`, getHeaders(accessToken)).then((resp) => {
    return resp.data;
  });
};

const getAPISandboxKey = async (accessToken: string) => {
  return await axios
    .get(`${API_URL}/users/tokens/sandbox`, getHeaders(accessToken))
    .then((resp) => {
      return resp.data;
    });
};

/************************************************
 *
 *
 * EVENT BOOKING
 *
 *
 **********************************************/

const getAvailableEventBookings = async (accessToken: string, accountId: number) => {
  return await axios
    .get(`${API_URL}/events/lists/bookings/${accountId}`, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const getEventBooking = async (accessToken: string, accountId: number, eventId: number) => {
  return await axios
    .get(`${API_URL}/events/lists/bookings/${accountId}/${eventId}`, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const getBookingRateCards = async (accessToken: string, accountId: number) => {
  return await axios
    .get(`${API_URL}/booking/cards/${accountId}`, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const getBookingCountries = async (accessToken: string) => {
  return await axios.get(`${API_URL}/booking/countries`, getHeaders(accessToken)).then((resp) => {
    return resp;
  });
};

const submitBookingQuote = async (accessToken: string, bookingDetails: BookingRequest) => {
  return await axios
    .post(`${API_URL}/booking/quote`, bookingDetails, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const getQuotePDF = async (accessToken: string, quoteId: number) => {
  const config = getPDFConfig(accessToken);
  return await axios.get(`${API_URL}/booking/quote/${quoteId}/pdf`, config).then((resp) => {
    return resp;
  });
};

const approveBookingQuote = async (accessToken: string, quoteId: number, payload: object) => {
  return await axios
    .post(`${API_URL}/booking/quote/${quoteId}/approve`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const getInvoicePDF = async (accessToken: string, quoteId: number) => {
  const config = getPDFConfig(accessToken);
  return await axios.get(`${API_URL}/booking/invoice/${quoteId}/pdf`, config).then((resp) => {
    return resp;
  });
};

/************************************************
 *
 *
 * ROLES RELATED
 *
 *
 **********************************************/

// Get List of Roles. Used in the Add/Edit User Modals.
const getRoles = async (accessToken: string) => {
  return await axios.get(`${API_URL}/roles`, getHeaders(accessToken)).then((resp) => {
    return resp.data;
  });
};

const getRoleByID = async (accessToken: string, roleId: number) => {
  return await axios.get(`${API_URL}/roles/${roleId}`, getHeaders(accessToken)).then((resp) => {
    return resp.data;
  });
};

const getScopes = async (accessToken: string) => {
  return await axios.get(`${API_URL}/scopes`, getHeaders(accessToken)).then((resp) => {
    return resp.data;
  });
};

/************************************************
 *
 *
 * DOCUMENTS RELATED
 *
 *
 **********************************************/

// Get List of Documents.
const getDocuments = async (accessToken: string, queryParams: object) => {
  const data = {
    ...getHeaders(accessToken),
    params: queryParams,
  };

  return await axios.get(`${API_URL}/documents`, data).then((resp) => {
    return resp.data;
  });
};

const getDocumentByID = async (accessToken: string, docId: string) => {
  const config = getPDFConfig(accessToken);
  return await axios.get(`${API_URL}/documents/${docId}`, config).then((resp) => {
    return resp;
  });
};

const createDocument = async (accessToken: string, payload: object) => {
  return await axios.post(`${API_URL}/documents`, payload, getHeaders(accessToken)).then((resp) => {
    return resp;
  });
};

const deleteDocumentByID = async (accessToken: string, docId: number) => {
  return await axios
    .delete(`${API_URL}/documents/${docId}`, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

/************************************************
 *
 *
 * USER RELATED
 *
 *
 **********************************************/

// Basic Users Query. Used on Manage Users page.
const getUsers = async (accessToken: string, queryParams: object) => {
  return await axios.post(`${API_URL}/users`, queryParams, getHeaders(accessToken)).then((resp) => {
    return resp.data.users;
  });
};

// Edit User by ID, used in the Edit User modal.
const getUserById = async (accessToken: string, userId: number) => {
  return await axios
    .get(`${API_URL}/users/${userId}/edit`, getHeaders(accessToken))
    .then((resp) => {
      const { user } = resp.data;

      return {
        id: user.id,
        disable_sso: user.disable_sso,
        dashboard_access_timeout: user.dashboard_access_timeout,
        account: user.account,
        email: user.email,
        name: user.name,
        role: {
          id: user.role.id,
          display_name: user.role.display_name,
        },
        platform_user: {
          account_id: user.platform_user.account_id,
          phone: user.platform_user.phone,
          phone_country_code: user.platform_user.phone_country_code,
          is_active: user.platform_user.is_active,
          create_campaigns: user.platform_user.create_campaigns,
          edit_campaigns: user.platform_user.edit_campaigns,
        },
        updated_at: user.updated_at,
      };
    });
};

// Make changes to user by ID, used for quick archive/unarchive.
const updateUserById = async (accessToken: string, userId: number, payload: object) => {
  return await axios
    .post(`${API_URL}/users/${userId}/update`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const updateUserSelf = async (accessToken: string, payload: object) => {
  return await axios
    .patch(`${API_URL}/users/self`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const updateUserSelfPassword = async (accessToken: string, payload: object) => {
  return await axios
    .post(`${API_URL}/users/change-password`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const createUser = async (accessToken: string, payload: object) => {
  return await axios
    .post(`${API_URL}/users/store`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const deleteUserById = async (accessToken: string, userId: number) => {
  return await axios.delete(`${API_URL}/users/${userId}`, getHeaders(accessToken)).then((resp) => {
    return resp;
  });
};

/************************************************
 *
 *
 * LOGIN RELATED
 *
 *
 **********************************************/

// Request new Password
const forgotPassword = async (email: string) => {
  return await axios
    .post(`${API_URL}/auth/forgot`, {
      email: email,
    })
    .then((resp) => {
      return resp;
    });
};

const updatePassword = async (
  email: string,
  password: string,
  password_confirmation: string,
  resetCode: string,
) => {
  return await axios
    .post(
      `${API_URL}/auth/reset`,
      {
        email: email,
        password: password,
        password_confirmation: password_confirmation,
        token: resetCode,
      },
      getHeaders(),
    )
    .then((resp) => {
      return resp;
    });
};

// Log In using Password
const logInWithPassword = async (email: string, password: string, remember: boolean) => {
  const params = {
    email: email,
    password: password,
    short_expiry: !remember,
  };

  return await axios.post(`${API_URL}/auth/login`, params).then((resp) => {
    return resp;
  });
};

// Logout
const logOutApi = async (accessToken: string) => {
  return await axios.get(`${API_URL}/auth/logout`, getHeaders(accessToken)).then((resp) => {
    return resp;
  });
};

// Check SSO access
const checkSSO = async (email: string) => {
  return await axios
    .post(`${API_URL}/auth/login/saml`, { email: email, from: 'cloudv2' }, getHeaders())
    .then((resp) => {
      return resp;
    });
};

// Log In using SSO provider response
const logInWithSSO = async (response: any, account: string) => {
  return await axios
    .post(`${API_URL}/saml/${account}/callback`, { response: response }, getHeaders())
    .then((resp) => {
      return resp;
    });
};

const refreshToken = async (accessToken: string) => {
  return await axios
    .post(`${API_URL}/auth/refresh-token`, { accessToken: accessToken }, getHeaders(accessToken))
    .then((resp) => {
      return resp.data;
    });
};

/************************************************
 *
 *
 * SENSOR RELATED
 *
 *
 **********************************************/

// Basic Sensors Query. Used on Manage Sensors page.
const getSensors = async (accessToken: string, queryParams: object) => {
  const data = {
    ...getHeaders(accessToken),
    params: queryParams,
  };

  return await axios.get(`${API_URL}/sensors`, data).then((resp) => {
    return resp.data.sensors;
  });
};

const createSensor = async (accessToken: string, payload: object) => {
  return await axios.post(`${API_URL}/sensors`, payload, getHeaders(accessToken)).then((resp) => {
    return resp;
  });
};

const renameSensors = async (accessToken: string, payload: object) => {
  return await axios
    .post(`${API_URL}/sensors/rename`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const moveSensors = async (accessToken: string, payload: object) => {
  return await axios
    .post(`${API_URL}/sensors/move`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const toggleSensorActivation = async (accessToken: string, payload: object) => {
  return await axios
    .post(`${API_URL}/sensors/activate`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const getSensorRangeOptions = async (accessToken: string, isStaff: boolean, sensorId: number) => {
  return await axios
    .get(`${API_URL}/sensors/${sensorId}/ranges`, getHeaders(accessToken))
    .then((resp) => {
      const data = resp.data.filter((range: SensorDistanceSettingType) => {
        if (isStaff) {
          return range.signal_strength >= -75;
        }
        return range.signal_strength >= -72;
      });

      return data;
    });
};

const snoozeEventSensors = async (
  accessToken: string,
  eventId: number | string,
  payload: object,
) => {
  return await axios
    .post(`${API_URL}/sensors/${eventId}/snooze`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

/************************************************
 *
 *
 * EVENT RELATED
 *
 *
 **********************************************/

// Basic Events Query. Used on Manage Events page.
const getEvents = async (accessToken: string, queryParams: object) => {
  return await axios
    .post(`${API_URL}/events`, queryParams, getHeaders(accessToken))
    .then((resp) => {
      return resp.data;
    });
};

const getEventById = async (accessToken: string, eventId: number) => {
  return await axios.get(`${API_URL}/events/${eventId}`, getHeaders(accessToken)).then((resp) => {
    return resp.data;
  });
};

const getEventSensors = async (accessToken: string, eventId: number) => {
  return await axios.get(`${API_URL}/sensors/${eventId}`, getHeaders(accessToken)).then((resp) => {
    return resp.data;
  });
};

const getEventLists = async (accessToken: string) => {
  return await axios.get(`${API_URL}/events/lists`, getHeaders(accessToken)).then((resp) => {
    return resp.data;
  });
};

const getEventLocations = async (accessToken: string) => {
  return await axios
    .get(`${API_URL}/events/lists/locations`, getHeaders(accessToken))
    .then((resp) => {
      return resp.data;
    });
};

const createEvent = async (accessToken: string, payload: object) => {
  return await axios
    .post(`${API_URL}/events/import`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const updateEvent = async (accessToken: string, eventId: number, payload: object) => {
  return await axios
    .patch(`${API_URL}/events/${eventId}`, payload, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const getEventCleanup = async (accessToken: string, eventId: number) => {
  return await axios
    .get(`${API_URL}/events/${eventId}/cleanup`, getHeaders(accessToken))
    .then((resp) => {
      return resp.data;
    });
};

const getEventReport = async (accessToken: string, payload: object) => {
  return await axios
    .post(`${API_URL}/spatial/reporting/report-download`, payload, getHeaders(accessToken, true))
    .then((resp) => {
      return resp;
    });
};

/************************************************
 *
 *
 * ANALYTICS RELATED
 *
 *
 **********************************************/

const getEventCompareWidgets = async (accessToken: string, payload: object) => {
  const data = {
    ...getHeaders(accessToken),
    params: payload,
  };

  return await axios.get(`${API_URL}/spatial/compare/widgets`, data).then((resp) => {
    return resp.data;
  });
};

const getReportedEvents = async (accessToken: string) => {
  return await axios.get(`${API_URL}/events/list`, getHeaders(accessToken)).then((resp) => {
    return resp.data;
  });
};

const getDemoEventAnalytics = async (accessToken: string) => {
  return await axios.get(`${API_URL}/events/demo`, getHeaders(accessToken)).then((resp) => {
    return resp;
  });
};

const getPremiumMetric = async (accessToken: string, eventId: number, widgetId: number) => {
  return await axios
    .get(`${API_URL}/spatial/event/${eventId}/premium-widgets/${widgetId}`, getHeaders(accessToken))
    .then((resp) => {
      return resp.data;
    });
};

/************************************************
 *
 *
 * ANALYTICS TEMPLATING
 *
 *
 **********************************************/

const getEventTemplates = async (accessToken: string, eventId: number) => {
  return await axios
    .get(`${API_URL}/spatial/event/${eventId}/templates`, getHeaders(accessToken))
    .then((resp) => {
      return resp.data;
    });
};

const getEventTemplateById = async (accessToken: string, eventId: number, templateId: number) => {
  return await axios
    .get(`${API_URL}/spatial/event/${eventId}/templates/${templateId}`, getHeaders(accessToken))
    .then((resp) => {
      return resp.data;
    });
};

const getWidgetById = async (accessToken: string, eventId: number, widgetId: number) => {
  return await axios
    .get(`${API_URL}/spatial/event/${eventId}/widgets/${widgetId}`, getHeaders(accessToken))
    .then((resp) => {
      return resp.data;
    });
};

const updateEventTemplate = async (
  accessToken: string,
  eventId: number,
  templateId: number,
  payload: object,
) => {
  return await axios
    .patch(
      `${API_URL}/spatial/event/${eventId}/templates/${templateId}`,
      payload,
      getHeaders(accessToken),
    )
    .then((resp) => {
      return resp;
    });
};

const getBenchmarkingDataByWidgetAndZone = async (
  accessToken: string,
  eventId: number,
  widgetId: number,
  zoneId: string | undefined,
) => {
  return await axios
    .get(
      `${API_URL}/spatial/event/${eventId}/widgets/${widgetId}${zoneId ? `?area[0][name]=Zone&area[0][value]=${zoneId}` : ''}`,
      getHeaders(accessToken),
    )
    .then((resp) => {
      return resp.data;
    });
};

/************************************************
 *
 *
 * FEATURE FLAGS
 *
 *
 **********************************************/

const getFeatureFlags = async (accessToken: string) => {
  return await axios.get(`${API_URL}/features`, getHeaders(accessToken)).then((resp) => {
    return resp.data;
  });
};

const addRoleToFeatureFlag = async (accessToken: string, featureFlagId: number, roleId: number) => {
  return await axios
    .post(
      `${API_URL}/features/${featureFlagId}/attach-role/${roleId}`,
      undefined,
      getHeaders(accessToken),
    )
    .then((resp) => {
      return resp;
    });
};

const removeRoleFromFeatureFlag = async (
  accessToken: string,
  featureFlagId: number,
  roleId: number,
) => {
  return await axios
    .delete(`${API_URL}/features/${featureFlagId}/detach-role/${roleId}`, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

const addAccountToFeatureFlag = async (
  accessToken: string,
  featureFlagId: number,
  accountId: number,
) => {
  return await axios
    .post(
      `${API_URL}/features/${featureFlagId}/attach-account/${accountId}`,
      undefined,
      getHeaders(accessToken),
    )
    .then((resp) => {
      return resp;
    });
};

const removeAccountFromFeatureFlag = async (
  accessToken: string,
  featureFlagId: number,
  accountId: number,
) => {
  return await axios
    .delete(
      `${API_URL}/features/${featureFlagId}/detach-account/${accountId}`,
      getHeaders(accessToken),
    )
    .then((resp) => {
      return resp;
    });
};

const toggleFeatureFlagForAllUsers = async (
  accessToken: string,
  featureFlagId: number,
  isActiveForAll: boolean,
) => {
  return await axios
    .patch(
      `${API_URL}/features/${featureFlagId}/toggle-all-users`,
      { is_active_for_all: isActiveForAll },
      getHeaders(accessToken),
    )
    .then((resp) => {
      return resp;
    });
};

/************************************************
 *
 *
 * Premium Benchmarking
 *
 *
 **********************************************/

const getBenchmarkingDataForEvent = async (
  accessToken: string,
  eventId: number,
  event_type: number[],
  verticals: number[],
  super_region: number[],
  accountId?: number,
  zoneId?: number,
) => {
  return await axios
    .get(`${API_URL}/spatial/benchmarking-zones/calculate/${eventId}`, {
      ...getHeaders(accessToken),
      params: {
        event_type_ids: event_type,
        vertical_ids: verticals,
        super_region_ids: super_region,
        account_id: accountId,
        zone_id: zoneId,
      },
    })
    .then((resp) => {
      return resp.data;
    });
};

/************************************************
 *
 *
 * OTHER
 *
 *
 **********************************************/

const sendContactEmail = async (accessToken: string, payload: object) => {
  return await axios
    .post(`${API_URL}/users/contact`, { message: payload }, getHeaders(accessToken))
    .then((resp) => {
      return resp;
    });
};

export {
  // Accounts
  getAccounts,
  getAccountsList,
  getAccountById,
  getAccountSensors,
  getAccountNew,
  updateAccountById,
  deleteAccountById,
  createAccount,
  getAccountProfileById,
  getVatValidation,
  updateAccountProfileById,
  getClientDetailsList,

  // Event Booking
  getAvailableEventBookings,
  getEventBooking,
  getBookingRateCards,
  getBookingCountries,
  submitBookingQuote,
  getQuotePDF,
  approveBookingQuote,
  getInvoicePDF,

  // Roles
  getRoles,
  getRoleByID,
  getScopes,

  // Documents
  getDocuments,
  getDocumentByID,
  createDocument,
  deleteDocumentByID,

  // Users
  getUsers,
  getUserById,
  updateUserById,
  updateUserSelf,
  updateUserSelfPassword,
  createUser,
  deleteUserById,

  // Login
  checkSSO,
  forgotPassword,
  logInWithPassword,
  logInWithSSO,
  refreshToken,
  updatePassword,
  logOutApi,

  // Sensors
  getSensors,
  createSensor,
  moveSensors,
  renameSensors,
  toggleSensorActivation,
  getSensorRangeOptions,
  snoozeEventSensors,

  // Events
  getEvents,
  getEventById,
  getEventSensors,
  getEventLists,
  getEventLocations,
  createEvent,
  updateEvent,
  getEventCleanup,
  getEventReport,

  // Analytics
  getEventCompareWidgets,
  getReportedEvents,
  getEventTemplates,
  getEventTemplateById,
  updateEventTemplate,
  getWidgetById,
  getDemoEventAnalytics,
  getBenchmarkingDataByWidgetAndZone,
  getPremiumMetric,

  // API
  requestAPIToken,
  getAPITokens,
  deleteKeyById,
  getAPIUsage,
  getAPISandboxKey,

  // Feature Flags
  getFeatureFlags,
  addRoleToFeatureFlag,
  removeRoleFromFeatureFlag,
  addAccountToFeatureFlag,
  removeAccountFromFeatureFlag,
  toggleFeatureFlagForAllUsers,

  // Premium Benchmarking
  getBenchmarkingDataForEvent,

  // Other
  sendContactEmail,
};
